import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { marketplaceApiRef } from '../plugin';
import { useQuery } from '@tanstack/react-query';
import { governanceApiRef } from '@agilelab/plugin-wb-governance';
import { extractIds } from '../utils';
import {
  PolicyViolationOutcome,
  PolicyViolationsCountMap,
} from '@agilelab/plugin-wb-governance-common';

export interface PolicyViolationsResult {
  individualViolations: PolicyViolationsCountMap;
  aggregatedViolations: PolicyViolationsCountMap;
  descendants?: {
    id: string;
    externalId: string;
    components?: {
      id: string;
      externalId: string;
    }[];
  }[];
}

export const useGetPolicyViolationsCountMapById = (
  externalId: string | undefined,
  environment: string | undefined,
) => {
  const marketplaceApi = useApi(marketplaceApiRef);
  const identityApi = useApi(identityApiRef);
  const governanceApi = useApi(governanceApiRef);

  const { data, isLoading } = useQuery({
    queryKey: ['policy-aggregation', environment, externalId],
    staleTime: 1 * 60 * 1000, // 1 minute
    queryFn: async (): Promise<PolicyViolationsResult> => {
      if (!externalId || !environment) {
        throw new Error('External ID and environment are required');
      }

      const descendants =
        await marketplaceApi.getConsumableDescendantsForEntityUrns({
          entityUrns: [externalId],
          environment: environment,
        });

      if (!descendants || descendants.length === 0) {
        return {
          individualViolations: new Map(),
          aggregatedViolations: new Map(),
        };
      }

      const root = descendants[0];

      const descendantIds = extractIds(descendants);

      const token = (await identityApi.getCredentials()).token;
      const individualViolations =
        await governanceApi.computePolicyViolationsCount(
          {
            environment: environment,
            resources: Array.from(new Set(descendantIds)),
          },
          { token },
        );

      const aggregatedViolations: PolicyViolationsCountMap = new Map();

      const normalizeOutcome = (
        outcome: PolicyViolationOutcome,
      ): PolicyViolationOutcome => {
        let normalizedOutcome: PolicyViolationOutcome;

        switch (outcome) {
          case 'info':
            normalizedOutcome = 'info';
            break;
          case 'warning':
          case 'not_blocking_error':
            normalizedOutcome = 'warning';
            break;
          case 'error':
            normalizedOutcome = 'error';
            break;
          default:
            normalizedOutcome = outcome;
        }

        return normalizedOutcome;
      };

      const aggregateViolations = (
        violations: Map<PolicyViolationOutcome, number>[],
      ) => {
        const aggregated = new Map<PolicyViolationOutcome, number>();

        for (const violationMap of violations) {
          for (const [outcome, count] of violationMap.entries()) {
            const normalizedOutcome = normalizeOutcome(outcome);
            const currentCount = aggregated.get(normalizedOutcome) ?? 0;
            aggregated.set(normalizedOutcome, currentCount + count);
          }
        }

        return aggregated;
      };

      const aggregateItemRecursively = (
        item: any,
      ): Map<PolicyViolationOutcome, number> => {
        const childViolations: Map<PolicyViolationOutcome, number>[] = [];
        if (item.components && Array.isArray(item.components)) {
          for (const component of item.components) {
            const componentAggregated = aggregateItemRecursively(component);

            aggregatedViolations.set(component.externalId, componentAggregated);

            childViolations.push(componentAggregated);
          }
        }

        const itemViolations =
          individualViolations.get(item.externalId) ?? new Map();

        return aggregateViolations([itemViolations, ...childViolations]);
      };

      const rootAggregated = aggregateItemRecursively(root);
      aggregatedViolations.set(externalId, rootAggregated);

      return {
        individualViolations,
        aggregatedViolations,
        descendants: descendants?.[0].components ?? [],
      };
    },
    enabled: Boolean(externalId) && Boolean(environment),
  });

  return {
    data,
    isLoading,
  };
};
